import React, { useState, useEffect, useRef } from "react";
import useForm from '../../../hooks/useForm'
import { FormField, Button, LoadingIcon } from '@prosolve/common-components-ui'
import { FaPlus, FaSave } from 'react-icons/fa';
import EditEventBlueprintScheduleModal from "./EditEventBlueprintScheduleModal";
import EventBlueprintScheduleDisplay from "./EventBlueprintScheduleDisplay";
import EventResourcesEditor from "../../EventView/EventTabContent/EventResources/EventResourcesEditor";
import useDataSessionBlueprints from '../../../dataHooks/useDataSessionBlueprints';
import { useNavigate } from "react-router-dom";
import { checkHasEditEventTemplatePermissions, getClientSubdomain } from '../../../utils';
import useSnackbar from "../../../hooks/useSnackbar";
import LoadingOverlay from "../../sharedComponents/LoadingOverlay";

export default () => {
  const urlParams = new URLSearchParams(window.location.search);
  const eventBlueprintId = urlParams.get('id');
  
  const clientSubdomain = getClientSubdomain()
  const isStarSubdomain = (clientSubdomain === 'star')

  const {
    fetchEventBlueprint,
    updateEventBlueprint,
    fetchProducts: fetchProductsHook
  } = useDataSessionBlueprints();

  const requiredFields = (
    isStarSubdomain ? ['name', 'defaultProduct', 'defaultNumberOfDays', 'defaultDeliveryPrice']
    : ['name']
  )
  const formSchema = {
    requiredFields: requiredFields,
    optionalFields: ['description', 'objective', 'image', 'blueprintDocumentLink', 'slideDeckLink', 'handoutsLink', 'assessmentName'],
    initData: {}
  }

  const formData = useForm(formSchema)
  const navigate = useNavigate();
  const snackbar = useSnackbar()

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingProducts, setIsLoadingProducts] = useState(true)
  const [sessions, setSessions] = useState([])
  const [products, setProducts] = useState([])
  const [productsFilter, setProductsFilter] = useState()
  const [editSessionsObj, setEditSessionsObj] = useState()
  const [lastNumDays, setLastNumDays] = useState()

  const defaultNumberOfDaysRef = useRef(null)

  const isReadOnly = false;

  const filteredProducts = products
    .filter(p => productsFilter ? p.name.toLowerCase().includes(productsFilter.toLowerCase()) : true)
    .map(p => ({ text: p.name, value: p.id }))
    .sort((p,p2) => p.text.toLowerCase().localeCompare(p2.text.toLowerCase()))

  const isValid = () => {
    if (formData.isValid()) {
      return true
    } else {
      return false
    }
  }

  const fetchSessionBlueprint = async () => {
    setIsLoading(true);
    try {
      const eBlueprint = await fetchEventBlueprint(eventBlueprintId);
      const { eventBlueprint = {}, eventBlueprintSchedule = {} } = eBlueprint;

      const responseSessions = eventBlueprintSchedule.sessions || [];

      const updateFormData = {
        ...eventBlueprint,
        eventSessionBlueprintId: eventBlueprintSchedule._id
      }

      formData.setValues(updateFormData)
      setSessions(responseSessions)
    } catch (e) {
      console.log(e)
      formData.setValues({})
      setSessions([])
    }
    setIsLoading(false);
  }

  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const products = await fetchProductsHook();
      setProducts(products)
    } catch (e) {
      console.log(e)
    }
    setIsLoadingProducts(false);
  }

  useEffect(() => {
    // Limit defaultNumberOfDays to 2 decimals
    const numDays = formData.values.defaultNumberOfDays
    if (lastNumDays !== numDays && numDays?.length > 0) {
      setLastNumDays(numDays)
      const matches = numDays.replace(/[^0-9.]/g, '').match(/(\d*\.?\d{0,2})/)
      const formattedNumDays = matches[0] || numDays
      defaultNumberOfDaysRef.current.setInput(formattedNumDays)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values])

  useEffect(() => {
    if (eventBlueprintId !== 'new') {
      fetchSessionBlueprint()
    } else {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBlueprintId])

  useEffect(() => {
    if (isStarSubdomain) {
      fetchProducts()
    } else {
      setIsLoadingProducts(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const performSave = async () => {
    setIsSaving(true)
    try {

      const eventBlueprint = {
        ...formData.values
      }

      const eventBlueprintSchedule = {
        name: formData.values.name,
        eventBlueprintId,
        sessions
      }

      if (formData.values.eventSessionBlueprintId) {
        delete eventBlueprint.eventSessionBlueprintId;
        eventBlueprintSchedule._id = formData.values.eventSessionBlueprintId;
      }

      const saveData = {
        eventBlueprint,
        eventBlueprintSchedule
      }

      await updateEventBlueprint(saveData);

      snackbar.newMessage({ 
        message: 'Successfully saved event template', 
        severity: 'success'
      })

      if (eventBlueprintId === 'new') {
        // navigate(`/design/eventTemplate?id=${response.eventBlueprintId}`);
        navigate(`/design`);
      } else {
        // Need to set the assessment we started from to the newly edited assessment to be our new latest and greatest
        fetchSessionBlueprint();
      }
      
    } catch (e) {
      console.log(e)
    }
    setIsSaving(false)
  }

  const onSave = async () => {
    if (isValid()) {
      performSave()
    }
  }

  const onUpdateResources = (resources) => {
    formData.values.resources = resources
  }

  const onEditSchedule = () => {
   setEditSessionsObj(sessions)
  }

  const onUpdateSessionsSchedule = (sessionsData) => {
    setSessions(sessionsData)
    setEditSessionsObj(undefined)
  }

  const hasEditEventTemplatePermissions = checkHasEditEventTemplatePermissions()
  if (!hasEditEventTemplatePermissions) {
    return <div></div>
  }

  return (
    <>
    {
      isSaving &&
      <LoadingOverlay 
        message='Updating...'
        backgroundColor='#ffffff' 
        width='calc(100vw - 15px)' 
        marginTop='0px' 
        marginLeft='0px' 
        transparent={true} 
      />
    }
    {
      isLoading || isLoadingProducts ?
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px'
      }}>
        <LoadingIcon />
      </div>
    :
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      padding: '20px'
    }}>
      {
        Boolean(editSessionsObj) &&
        <EditEventBlueprintScheduleModal
          sessions={editSessionsObj}
          // eventId={eventData.id}
          onClose={() => setEditSessionsObj(undefined)}
          onSuccess={onUpdateSessionsSchedule}
        />
      }
      <div style={{
        display: 'flex',
        marginBottom: '20px'
      }}>
        <div style={{
          color: '#565656',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          {eventBlueprintId === 'new' ? 'New Event Template' : (formData.values.name || '-')}
        </div>
        <div style={{ flex: 1 }}>&nbsp;</div>
        <Button
          style={{
            display: 'inline-flex',
            width: '125px',
            padding: '8px 16px'
          }}
          // $colored={true}
          $disabled={isSaving || isReadOnly}
          onClick={() => {
            if (!isSaving && !isReadOnly) {
              onSave()
            }
          }}
        >
          <FaSave style={{ marginRight: '10px', color: '#0280c6' /* color: '#ffffff' */ }} />
          <span style={{ fontWeight: 'bold', color: '#565656' /* color: '#ffffff' */ }}>{'Save'}</span>
        </Button>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          columnGap: '15px',
          rowGap: '15px',
        }}>
          <FormField
            fieldId='name'
            type='text'
            label='Name'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='description'
            type='textarea'
            label='Description'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='objective'
            type='textarea'
            label='Objective'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='image'
            type='text'
            label='Image'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='blueprintDocumentLink'
            type='text'
            label='Blueprint Document Link'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='slideDeckLink'
            type='text'
            label='Slide Deck Link'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='handoutsLink'
            type='text'
            label='Handouts Link'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='assessmentName'
            type='text'
            label='Assessment Name'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='defaultProduct'
            type='search'
            options={filteredProducts}
            label='Default Product'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
            maxHeight={'200px'}
            onSearch={s => setProductsFilter(s)}
          />
          <FormField
            ref={defaultNumberOfDaysRef}
            fieldId='defaultNumberOfDays'
            type='number'
            label='Default Number Of Days'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='defaultDeliveryPrice'
            type='currency'
            label='Default Delivery Price'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
        </div>
      </div>
      <div style={{
        marginBottom: '20px'
      }}>
        <EventResourcesEditor resources={formData?.values.resources} onUpdate={onUpdateResources} />
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{
          flex: 1,
          color: '#565656',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Manage Schedule
        </div>
        <Button
          style={{
            display: 'inline-flex',
            width: '125px',
            padding: '8px 16px'
          }}
          onClick={onEditSchedule}
        >
          <FaPlus style={{ marginRight: '10px', color: '#0280c6' }} />
          <span style={{ fontWeight: 'bold', color: '#565656' }}>{'Edit Schedule'}</span>
        </Button>
      </div>
      <EventBlueprintScheduleDisplay sessions={sessions} />
    </div>
    }
    </>
  )
}